import React, { useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

const ShiftedTasksHistoryModal = ({ onClose, taskId }) => {
  const [shiftedTasks, setShiftedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [departments, setDepartments] = useState({});
  const [users, setUsers] = useState({});
  const limit = 10;

  // API configuration
  const API_BASE_URL = 'https://slateblue-mallard-430836.hostingersite.com/api';
  const authToken = localStorage.getItem('authToken')?.replace(/['"]/g, '').trim();

  const getCompanyId = useCallback(async () => {
    const token = localStorage.getItem('authToken');

    if (token) {
      try {
        const decodedToken = jwtDecode(token);

        if (decodedToken && decodedToken.role === 'company') {
          if (decodedToken.id) {
            return decodedToken.id;
          }
        } else {
          if (decodedToken && decodedToken.companyId) {
            return decodedToken.companyId;
          }
        }

        if (decodedToken && (decodedToken.userId || decodedToken.id)) {
          const userId = decodedToken.userId || decodedToken.id;
        }
      } catch (error) {
        console.error('JWT decoding failed:', error);
      }
    }

    const storedCompanyId = localStorage.getItem('companyId');
    if (storedCompanyId) {
      return storedCompanyId;
    }

    return null;
  }, []);

  // Fetch departments
  const fetchDepartments = useCallback(async () => {
    try {
      const companyId = await getCompanyId();
      const token = authToken;

      if (!companyId || !token) {
        throw new Error('Missing company ID or authentication token');
      }

      const response = await fetch(`${API_BASE_URL}/departments/company/${companyId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch departments: ${response.status}`);
      }

      const data = await response.json();
      const deptList = Array.isArray(data) ? data : (data.data || data.departments || []);

      const departmentsMap = {};
      deptList.forEach(dept => {
        if (dept && (dept._id || dept.id)) {
          const id = dept._id || dept.id;
          const name = dept.name || dept.departmentName || dept.title || id;
          departmentsMap[String(id)] = name;  // Ensure string key
        }
      });

      setDepartments(departmentsMap);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, [API_BASE_URL, authToken, getCompanyId]);

  // Fetch users (enhanced for better name extraction and error handling)
  const fetchUsers = useCallback(async () => {
    try {
      const companyId = await getCompanyId();
      const token = authToken;

      if (!companyId || !token) return;

      const response = await fetch(`${API_BASE_URL}/employees/company/${companyId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const data = await response.json();
      let usersArray = [];

      // Handle various response structures
      if (Array.isArray(data)) {
        usersArray = data;
      } else if (data.data && Array.isArray(data.data)) {
        usersArray = data.data;
      } else if (data.employees && Array.isArray(data.employees)) {
        usersArray = data.employees;
      } else if (data.users && Array.isArray(data.users)) {
        usersArray = data.users;
      }

      console.log('Raw users array (first item):', usersArray[0]);  // Debug first employee

      const userMap = {};
      usersArray.forEach(user => {
        if (!user) return;
        const id = user._id || user.id;
        if (!id) return;

        // Stringify ID as key (ensures match with backend IDs)
        const idStr = String(id);

        // Extract name from multiple possible fields
        const fullName = user.teamMemberName ||
          [user.firstName || '', user.lastName || ''].filter(Boolean).join(' ').trim() ||
          user.name ||
          user.fullName ||
          user.displayName ||
          user.email ||
          idStr;  // Fallback to ID

        userMap[idStr] = fullName;
      });

      console.log('Populated users map (keys):', Object.keys(userMap));  // Debug: Log all keys to check if backend ID exists
      console.log('Sample map entry:', Object.entries(userMap)[0]);  // Debug sample
      setUsers(userMap);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers({});  // Fallback to empty map
    }
  }, [API_BASE_URL, authToken, getCompanyId]);

  // Fetch shifted tasks
  const fetchShiftedTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const companyId = await getCompanyId();
      const token = authToken;

      if (!token) {
        throw new Error('No authentication token found');
      }

      // Try original endpoint
      try {
        const result = await tryOriginalEndpoint(companyId, token);
        if (result.success) {
          console.log("result", result);

          setShiftedTasks(result.tasks);
          setTotalPages(result.totalPages);
          setTotalItems(result.totalItems);
          return;
        }
      } catch (error) {
        console.log('Original endpoint failed:', error.message);
        throw error;  // Re-throw to trigger error state
      }

    } catch (error) {
      console.error('All approaches failed:', error);
      setError('Unable to load shifted tasks. The API endpoint has a server error.');
      setShiftedTasks([]);  // Clear tasks on error
    } finally {
      setLoading(false);
    }
  }, [currentPage, authToken, getCompanyId, taskId, API_BASE_URL]);

  // Try original endpoint with better error handling (matches backend getReassignedTasksForCompany)
  const tryOriginalEndpoint = async (companyId, token) => {
    let url = `${API_BASE_URL}/taskstatusupdate/reassigned-tasks?page=${currentPage}&limit=${limit}`;

    // Backend auto-resolves company from user, but add if needed
    if (companyId) url += `&companyId=${companyId}`;

    // NEW: Add taskId filter if provided (extend backend to support if needed) 
    if (taskId) url += `&taskId=${taskId}`;

    console.log('Fetching shifted tasks from:', url);  // Debug

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token
      },
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);  // Debug full error
      throw new Error(errorText || `HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('Full API Response:', data);  // Debug full structure

    // Backend structure: { success, page, limit, totalCount, hasMore, reassignedTasks: [...] }
    let tasks = data.reassignedTasks || data.data || data.tasks || [];
    console.log("task--------------------", tasks);  // Debug tasks array

    // UPDATED: Simplified processing - backend now provides full objects for shiftedBy and oldAssignee
    // No need for Raw/Obj separation; directly use the populated objects
    const processedTasks = tasks.map(task => {
      console.log('Processing task:', task._id);  // Debug
      console.log('Raw shiftedBy object:', task.shiftedBy);  // Debug populated shiftedBy
      console.log('Raw oldAssignee object:', task.oldAssignee);  // Debug populated oldAssignee

      return {
        ...task,  // Spread all fields, including shiftedBy and oldAssignee as full objects
        // Task details (backend: task object)
        taskTitle: task.task?.title || task.title || 'Untitled',
        taskDescription: task.task?.description || task.description || '',
        taskDepartment: task.task?.department || task.department || null,
        taskPriority: task.task?.priority || task.priority || 'medium',
        taskAssignedTo: task.task?.assignedTo || task.assignedTo || [],  // Array of IDs (for "To")
        // Description as shift reason
        shiftReason: task.description || task.shiftReason || '',
        // Dates
        updatedAt: task.updatedAt || task.createdAt || new Date().toISOString(),
        status: task.status || 'reassigned'
      };
    });

    console.log('Processed tasks (first shiftedBy):', processedTasks[0]?.shiftedBy);  // Debug processed
    console.log('Processed tasks (first oldAssignee):', processedTasks[0]?.oldAssignee);  // Debug

    return {
      success: true,
      tasks: processedTasks,
      totalPages: Math.ceil((data.totalCount || tasks.length) / limit),
      totalItems: data.totalCount || tasks.length
    };
  };


  // Load data with error recovery
  useEffect(() => {
    const loadData = async () => {
      try {
        // Ensure users are fetched first (sequential for dependency)
        await fetchUsers();
        await fetchDepartments();
        await fetchShiftedTasks();
      } catch (error) {
        console.error('Error in data loading sequence:', error);
        setError('Failed to load data. Please check your connection and try again.');
      }
    };

    loadData();
  }, [fetchDepartments, fetchUsers, fetchShiftedTasks]);

  // Helper functions
  const getDepartmentName = (departmentField) => {
    if (!departmentField) return 'Unknown Department';

    const map = departments || {};
    const isObjectId = (val) => typeof val === 'string' && /^[a-fA-F0-9]{24}$/.test(val.trim());

    const resolveOne = (d) => {
      if (!d) return null;
      if (typeof d === 'string') {
        if (isObjectId(d) && map[d]) return map[d];
        return map[d] || d;
      }
      if (typeof d === 'object' && d !== null) {
        const id = d._id || d.id;
        const name = d.name || d.departmentName;
        if (name) return name;
        if (id && map[String(id)]) return map[String(id)];
      }
      return null;
    };

    if (Array.isArray(departmentField)) {
      const names = departmentField
        .map(resolveOne)
        .filter(Boolean);
      return names.length ? Array.from(new Set(names)).join(', ') : 'Unknown Department';
    }

    return resolveOne(departmentField) || 'Unknown Department';
  };

  // Enhanced general user name helper (better raw ID handling)
  const getUserName = (userField) => {
    console.log("getUser Name - Input:", userField);  // Debug input

    if (!userField) return 'Unknown';

    if (Array.isArray(userField)) {
      if (userField.length === 0) return 'Unknown';
      const first = userField[0];
      if (userField.length > 1) {
        const firstName = getUserName(first);
        return `${firstName} +${userField.length - 1}`;
      }
      return getUserName(first);
    }

    if (typeof userField === 'object' && userField !== null) {
      // UPDATED: Backend now provides full objects with teamMemberName, firstName, etc.
      const id = userField._id || userField.id;
      const fullName = [
        userField.firstName,
        userField.lastName
      ].filter(Boolean).join(' ').trim();

      const display = userField.teamMemberName ||  // Priority: teamMemberName (as in backend sample)
        userField.fullName ||
        fullName ||
        userField.name ||
        userField.displayName ||
        userField.email ||
        (id ? users[String(id)] : '') ||  // Fallback to users map if needed
        id ||
        'Unknown';

      console.log("getUser Name - From object:", display);  // Debug
      return display;
    }

    // Raw string ID fallback (for assignees "To")
    if (typeof userField === 'string') {
      const idStr = String(userField);
      const fromMap = users[idStr];
      console.log("getUser Name - Raw ID from map:", fromMap, "ID:", idStr);  // Debug
      if (fromMap) return fromMap;

      // Fallback: Truncated ID
      return idStr.substring(0, 8) + '...' || 'Unknown ID';
    }

    return 'Unknown';
  };


  // UPDATED: Simplified getShiftedByDisplay (backend now always provides full object in task.shiftedBy)
  const getShiftedByDisplay = (t) => {
    console.log("getShiftedByDisplay - Task shiftedBy:", t?.shiftedBy);  // Debug

    if (!t || !t.shiftedBy) return 'Unknown';

    // Backend provides full object directly in shiftedBy
    const shiftedByObj = t.shiftedBy;  // Direct access (no need for shiftedByObj fallback)
    if (shiftedByObj && typeof shiftedByObj === 'object') {
      // UPDATED: Extract from backend structure (teamMemberName preferred, as in sample)
      const fullName = [
        shiftedByObj.firstName,
        shiftedByObj.lastName
      ].filter(Boolean).join(' ').trim();

      const displayName = shiftedByObj.teamMemberName ||  // Priority: teamMemberName (e.g., "Company Admin", "saiii")
        shiftedByObj.fullName ||
        fullName ||
        shiftedByObj.name ||
        shiftedByObj.email ||
        shiftedByObj._id?.substring(0, 8) + '...' ||  // Fallback to truncated ID
        'Unknown Shifter';

      console.log("Extracted shiftedBy name:", displayName);  // Debug
      return displayName;
    }

    // Fallback if somehow not object (unlikely now)
    return getUserName(t.shiftedBy) || 'Unknown';
  };

  // UPDATED: Get old assignee display for "From" (now uses direct task.oldAssignee object)
  const getOldAssigneeDisplay = (t) => {
    console.log("getOldAssigneeDisplay - Task oldAssignee:", t?.oldAssignee);  // Debug

    if (!t || !t.oldAssignee) return 'Unknown';

    // Backend provides full object directly in oldAssignee
    const oldAssigneeObj = t.oldAssignee;
    if (oldAssigneeObj && typeof oldAssigneeObj === 'object') {
      // Same extraction as getUser Name (teamMemberName preferred)
      return getUserName(oldAssigneeObj);
    }

    // Fallback
    return getUserName(t.oldAssigneeId || t.oldAssigneeRaw || t.oldAssignee) || 'Unknown';
  };

  // Enhanced: Get new assignee display (handles array of IDs from backend task.assignedTo)
  const getNewAssigneeDisplay = (t) => {

    if (!t) return 'Unknown';

    // Backend: task.assignedTo is array of raw IDs (not populated)
    let assignees = t.taskAssignedTo || t.assignedTo || t.newAssigneeId || t.newAssignee || t.task?.assignedTo || [];

    if (Array.isArray(assignees)) {
      if (assignees.length === 0) return 'No Assignee';

      // Map IDs to names using users map
      const assigneeNames = assignees.map(id => {
        if (typeof id === 'object' && id) {
          // If somehow populated (rare)
          return getUserName(id);
        } else {
          // Raw ID: Use users map
          const idStr = String(id);
          return users[idStr] || idStr || 'Unknown';
        }
      }).filter(Boolean);

      if (assigneeNames.length === 0) return 'Unknown';

      // Display first + count if multiple
      const firstName = assigneeNames[0];
      return assignees.length > 1 ? `${firstName} +${assignees.length - 1}` : firstName;
    }

    // Single object or ID fallback
    return getUserName(assignees) || 'Unknown';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in-progress':
      case 'in progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'reassigned':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ zIndex: '999999' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto" style={{ background: '#F8F9FA' }}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 z-10 rounded-t-2xl" style={{ background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-600 flex items-center justify-center mr-4">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900" style={{ color: '#2C2C2C' }}>
                  {taskId ? 'Task Shift History' : 'Shifted Tasks History'}
                </h2>
                <p className="text-gray-500 text-sm" style={{ color: '#6E6E6E' }}>
                  {taskId ? 'History of all shifts for this task' : 'All tasks that have been shifted between team members'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              style={{ background: '#F5F6FA' }}
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-8">
          {/* Backend Error Notice */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6" style={{ background: '#FEF2F2', borderColor: '#FECACA' }}>
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <p className="text-red-800 text-sm font-medium" style={{ color: '#DC2626' }}>Error loading data</p>
                  <p className="text-red-700 text-xs mt-1" style={{ color: '#DC2626' }}>{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mb-4"></div>
              <p className="text-gray-700" style={{ color: '#2C2C2C' }}>Loading shifted tasks history...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && shiftedTasks.length === 0 && !error && (
            <div className="bg-white rounded-xl p-8 text-center border border-gray-200" style={{ background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ color: '#2C2C2C' }}>No Shifted Tasks Found</h3>
              <p className="text-gray-600" style={{ color: '#6E6E6E' }}>
                {taskId
                  ? 'This task has not been shifted to another team member yet.'
                  : 'No tasks have been shifted between team members yet.'
                }
              </p>
            </div>
          )}

          {/* Results Summary */}
          {!loading && shiftedTasks.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6" style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-blue-800 text-sm font-medium" style={{ color: '#1E40AF' }}>
                  Found {totalItems} shifted task{totalItems !== 1 ? 's' : ''}
                  {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
                </p>
              </div>
            </div>
          )}

          {/* Shifted Tasks List */}
          {!loading && shiftedTasks.length > 0 && (
            <div className="space-y-4">
              {shiftedTasks.map((task) => (
                <div
                  key={task._id || task.id}
                  className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all duration-200"
                  style={{ background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h4 className="font-semibold text-gray-900 text-lg" style={{ color: '#2C2C2C' }}>
                          {task.task?.title || task.taskTitle || 'Untitled Task'}
                        </h4>
                        <div className="flex gap-2">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(task.status)}`}>
                            {task.status || 'reassigned'}
                          </span>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getPriorityClass(task.task?.priority)}`}>
                            {task.task?.priority || 'Medium'}
                          </span>
                        </div>
                      </div>

                      {/* Shift Details Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-3">
                          <div>
                            <span className="font-medium text-gray-700 block mb-1" style={{ color: '#2C2C2C' }}>Shifted by</span>
                            <div className="flex items-center text-gray-900" style={{ color: '#2C2C2C' }}>
                              <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                                <svg className="w-3 h-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <span title={`Raw shiftedBy: ${JSON.stringify(task.shiftedByRaw || task.shiftedBy)} | From users map: ${users[String(task.shiftedByRaw || task.shiftedBy)] || 'N/A'}`}>
                                {getShiftedByDisplay(task)}
                              </span>
                            </div>
                          </div>

                          <div>
                            <span className="font-medium text-gray-700 block mb-1" style={{ color: '#2C2C2C' }}>Shift date</span>
                            <div className="flex items-center text-gray-900" style={{ color: '#2C2C2C' }}>
                              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                              </div>
                              {formatDate(task.updatedAt || task.createdAt)}
                            </div>
                          </div>

                          <div>
                            <span className="font-medium text-gray-700 block mb-1" style={{ color: '#2C2C2C' }}>Department</span>
                            <div className="flex items-center text-gray-900" style={{ color: '#2C2C2C' }}>
                              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                </svg>
                              </div>
                              {getDepartmentName(task.task?.department || task.department)}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <span className="font-medium text-gray-700 block mb-1" style={{ color: '#2C2C2C' }}>From</span>
                            <div className="flex items-center text-gray-900" style={{ color: '#2C2C2C' }}>
                              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-2">
                                <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                </svg>
                              </div>
                              <span title={`Raw oldAssigneeId: ${JSON.stringify(task.oldAssigneeRaw || task.oldAssigneeId)} | From users map: ${users[String(task.oldAssigneeRaw || task.oldAssigneeId)] || 'N/A'}`}>
                                {getUserName(task.oldAssigneeRaw || task.oldAssigneeId || task.oldAssignee || task.previousAssigneeId || task.previousAssignee) || task.oldAssigneeName || 'Unknown'}
                              </span>
                            </div>
                          </div>

                          <div>
                            <span className="font-medium text-gray-700 block mb-1" style={{ color: '#2C2C2C' }}>To</span>
                            <div className="flex items-center text-gray-900" style={{ color: '#2C2C2C' }}>
                              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                                <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                                </svg>
                              </div>
                              <span title={`Raw assignedTo: ${JSON.stringify(task.taskAssignedTo || task.assignedTo)} | From users map: ${JSON.stringify((task.taskAssignedTo || task.assignedTo)?.map(id => users[String(id)]))}`}>
                                {getNewAssigneeDisplay(task)}
                              </span>
                            </div>
                          </div>


                        </div>
                      </div>

                      {/* Shift Reason Section (Orange Box) */}
                      {(task.shiftReason || task.description) && (
                        <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg" style={{ background: '#FFF7ED', borderColor: '#FED7AA' }}>
                          <div className="flex items-start">
                            <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                              <svg className="w-3 h-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <span className="font-medium text-orange-800 block mb-1" style={{ color: '#C2410C' }}>Reason for Shift</span>
                              <p className="text-orange-700 text-sm leading-relaxed" style={{ color: '#EA580C' }}>
                                {task.shiftReason || task.description || 'No reason provided'}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Attachments Section */}
                      {((task.task?.images && task.task.images.length > 0) ||
                        (task.task?.audios && task.task.audios.length > 0) ||
                        (task.task?.files && task.task.files.length > 0)) && (
                          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg" style={{ background: '#EFF6FF', borderColor: '#BFDBFE' }}>
                            <div className="flex items-start mb-3">
                              <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                                <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.586-6.586a4 4 0 00-5.656-5.656l-6.586 6.586a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                              </div>
                              <div className="flex-1">
                                <span className="font-medium text-blue-800 block mb-2" style={{ color: '#1E40AF' }}>Attachments</span>
                                <div className="space-y-2">
                                  {/* Images */}
                                  {task.task?.images && task.task.images.length > 0 && (
                                    <div>
                                      <span className="text-xs font-medium text-blue-700 block mb-1">Images ({task.task.images.length})</span>
                                      <div className="flex flex-wrap gap-2">
                                        {task.task.images.map((img, idx) => (
                                          <a
                                            key={idx}
                                            href={img.url || img}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                          >
                                            {img.originalName || `Image ${idx + 1}`}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {/* Audio Files */}
                                  {task.task?.audios && task.task.audios.length > 0 && (
                                    <div>
                                      <span className="text-xs font-medium text-blue-700 block mb-1">Audio ({task.task.audios.length})</span>
                                      <div className="flex flex-wrap gap-2">
                                        {task.task.audios.map((audio, idx) => (
                                          <a
                                            key={idx}
                                            href={audio.url || audio}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                          >
                                            Ž {audio.originalName || `Audio ${idx + 1}`}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {/* Files (PDFs, DOCX, etc.) */}
                                  {task.task?.files && task.task.files.length > 0 && (
                                    <div>
                                      <span className="text-xs font-medium text-blue-700 block mb-1">Files ({task.task.files.length})</span>
                                      <div className="flex flex-wrap gap-2">
                                        {task.task.files.map((file, idx) => (
                                          <a
                                            key={idx}
                                            href={file.url || file}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                                          >
                                            📄 {file.originalName || `File ${idx + 1}`}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                style={{ background: '#FFFFFF', borderColor: '#E2E8F0', color: '#2C2C2C' }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>

              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600" style={{ color: '#6E6E6E' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <span className="text-xs text-gray-400">
                  ({totalItems} total)
                </span>
              </div>

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                style={{ background: '#FFFFFF', borderColor: '#E2E8F0', color: '#2C2C2C' }}
              >
                Next
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShiftedTasksHistoryModal;


